import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Card, Badge, Modal, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { useAuthStore } from '@/stores/auth';
import { useRole } from '@/hooks/useRole';
import styles from './ExcelTemplates.module.css';

/* ── Types ── */

interface ExcelField {
  id: string;
  cell_ref: string;
  merge_range: string | null;
  sheet_name?: string;
  label: string;
  sub_label?: string;
  field_type: 'text' | 'number' | 'date' | 'select' | 'checkbox';
  db_mapping: string | null;
  required: boolean;
  order: number;
}

interface MappingEntry {
  field_id: string;
  source_table: string;
  source_column: string;
  transform: string | null;
}

interface ExcelTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  file_name: string;
  file_size: number;
  sheet_name: string;
  row_count: number;
  col_count: number;
  fields: ExcelField[];
  mappings: MappingEntry[];
  merge_cells: unknown[];
  status: string;
  published_at: string | null;
  is_locked: boolean;
  current_version: number;
  created_at: string;
  updated_at: string;
}

/* ── Constants ── */

const CATEGORIES: Record<string, string> = {
  all: '全て',
  important_explanation: '重要事項説明書',
  subsidy_application: '助成金申請書',
  lease: '賃貸借契約書',
  sales: '売買契約書',
  management: '管理委託契約書',
  notification: '届出書',
  other: 'その他',
};

const FIELD_TYPES: Record<string, string> = {
  text: 'テキスト',
  number: '数値',
  date: '日付',
  select: '選択',
  checkbox: 'チェック',
};

const MAPPABLE_TABLES: Record<string, string> = {
  properties: '物件',
  customers: '顧客',
  contracts: '契約',
  tenant: 'テナント設定',
};

/** AI 作成の解析モード。価格は入力/出力の 100万トークンあたり (USD) */
const AI_MODES: { key: string; icon: string; label: string; price: string; desc: string }[] = [
  { key: 'local', icon: '📄', label: 'APIなし（ローカル）', price: '$0', desc: 'xlsx パーサ + 正規表現。無料だが複雑なテーブルは未検出の場合あり' },
  { key: 'gemini-flash', icon: '⚡', label: 'Gemini 2.5 Flash', price: '$0.15/$0.60', desc: 'ファイル直接送信。最安のAI解析' },
  { key: 'gemini-pro', icon: '🧠', label: 'Gemini 2.5 Pro', price: '$1.25/$10', desc: 'ファイル直接送信。複雑なテーブルも抽出' },
  { key: 'claude-haiku-4-5', icon: '🍃', label: 'Claude Haiku 4.5', price: '$1/$5', desc: 'テキスト送信。高速・低コスト' },
  { key: 'claude-sonnet-5', icon: '🎵', label: 'Claude Sonnet 5', price: '$3/$15', desc: 'テキスト送信。高精度' },
  { key: 'claude-opus-4-8', icon: '🧭', label: 'Claude Opus 4.8', price: '$5/$25', desc: 'テキスト送信。最高精度' },
];

/* ── Helper ── */

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/* ── Component ── */

export function ExcelTemplates() {
  const navigate = useNavigate();
  const { isAdmin } = useRole();
  const canEdit = isAdmin;
  const [filter, setFilter] = useState('all');
  const { data: templates, loading, refetch } = useApi<ExcelTemplate[]>(
    `/console/excel-templates${filter !== 'all' ? `?category=${filter}` : ''}`
  );
  const { data: mappableColumns } = useApi<Record<string, Record<string, string>>>('/console/excel-templates/mappable-columns');

  // Upload state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadCategory, setUploadCategory] = useState('other');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sheetList, setSheetList] = useState<string[]>([]);
  const [selectedSheets, setSelectedSheets] = useState<Record<string, boolean>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // AI 作成 (解析モード選択のみ。解析処理は未実装)
  const [showAiCreate, setShowAiCreate] = useState(false);
  const [aiMode, setAiMode] = useState('local');

  /** ファイル選択後にシート名一覧を取得 */
  const fetchSheetNames = useCallback(async (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (ext !== 'xlsx' && ext !== 'xls') { setSheetList([]); setSelectedSheets({}); return; }
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const { token } = useAuthStore.getState();
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${base}/console/excel-templates/sheets`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (data.sheets) {
        setSheetList(data.sheets);
        const init: Record<string, boolean> = {};
        data.sheets.forEach((s: string) => { init[s] = true; });
        setSelectedSheets(init);
      }
    } catch { setSheetList([]); setSelectedSheets({}); }
  }, []);

  // Detail / Edit state
  const [selected, setSelected] = useState<ExcelTemplate | null>(null);
  const [editTab, setEditTab] = useState<'fields' | 'mappings' | 'preview'>('fields');
  const [editFields, setEditFields] = useState<ExcelField[]>([]);
  const [editMappings, setEditMappings] = useState<MappingEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const previewRef = useRef<HTMLIFrameElement>(null);

  // Confirm publish
  const [showPublish, setShowPublish] = useState(false);

  /* ── Load preview HTML ── */
  const loadPreview = useCallback(async (templateId: string) => {
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const { token } = useAuthStore.getState();
      const res = await fetch(`${base}/console/excel-templates/${templateId}/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const html = await res.text();
      setPreviewHtml(html);
    } catch { setPreviewHtml('<p style="color:red">プレビューの取得に失敗しました</p>'); }
  }, []);

  /** フィールドクリック時にプレビュータブへ切替+該当セルへスクロール */
  const scrollToField = useCallback((fieldId: string) => {
    if (!selected) return;
    setEditTab('preview');
    loadPreview(selected.id).then(() => {
      setTimeout(() => {
        previewRef.current?.contentWindow?.postMessage({ type: 'scrollToField', fieldId }, '*');
      }, 500);
    });
  }, [selected, loadPreview]);

  /* ── Upload ── */

  const handleUpload = useCallback(async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const { token } = useAuthStore.getState();
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadName || uploadFile.name.replace(/\.\w+$/, ''));
      formData.append('category', uploadCategory);
      formData.append('description', uploadDescription);
      // 選択シートを送信
      const sheets = Object.entries(selectedSheets).filter(([, v]) => v).map(([k]) => k);
      if (sheets.length > 0) formData.append('sheets', JSON.stringify(sheets));

      const res = await fetch(`${base}/console/excel-templates/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) { toast(data.error || 'アップロードに失敗しました', 'error'); return; }
      toast(`${data.name} をアップロードしました`);
      if (data.warnings) data.warnings.forEach((w: string) => toast(w, 'info'));
      setShowUpload(false);
      setUploadFile(null);
      setUploadName('');
      setUploadDescription('');
      setSheetList([]);
      setSelectedSheets({});
      refetch();
    } catch { toast('アップロードに失敗しました', 'error'); }
    finally { setUploading(false); }
  }, [uploadFile, uploadName, uploadCategory, uploadDescription, selectedSheets, refetch]);

  /* ── Select template for editing ── */

  const openDetail = useCallback(async (tpl: ExcelTemplate) => {
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const { token } = useAuthStore.getState();
      const res = await fetch(`${base}/console/excel-templates/${tpl.id}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const full = await res.json() as ExcelTemplate;
      setSelected(full);
      setEditFields(Array.isArray(full.fields) ? full.fields : []);
      setEditMappings(Array.isArray(full.mappings) ? full.mappings : []);
      setEditTab('fields');
    } catch { toast('テンプレート詳細の取得に失敗しました', 'error'); }
  }, []);

  /* ── Save fields ── */

  const saveFields = useCallback(async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const { token } = useAuthStore.getState();
      await fetch(`${base}/console/excel-templates/${selected.id}/fields`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields: editFields }),
      });
      toast('フィールド定義を保存しました');
      refetch();
    } catch { toast('保存に失敗しました', 'error'); }
    finally { setSaving(false); }
  }, [selected, editFields, refetch]);

  /* ── Save mappings ── */

  const saveMappings = useCallback(async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const { token } = useAuthStore.getState();
      const res = await fetch(`${base}/console/excel-templates/${selected.id}/mappings`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mappings: editMappings }),
      });
      const data = await res.json();
      if (!res.ok) { toast(data.error || '保存に失敗しました', 'error'); return; }
      toast(`マッピング定義を保存しました (v${data.version})`);
      refetch();
    } catch { toast('保存に失敗しました', 'error'); }
    finally { setSaving(false); }
  }, [selected, editMappings, refetch]);

  /* ── Publish ── */

  const handlePublish = useCallback(async () => {
    if (!selected) return;
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const { token } = useAuthStore.getState();
      await fetch(`${base}/console/excel-templates/${selected.id}/publish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      toast('テンプレートを公開しました');
      setShowPublish(false);
      setSelected(null);
      refetch();
    } catch { toast('公開に失敗しました', 'error'); }
  }, [selected, refetch]);

  /* ── Delete ── */

  const handleDelete = useCallback(async (id: string) => {
    if (!confirm('このテンプレートを削除しますか？')) return;
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const { token } = useAuthStore.getState();
      const res = await fetch(`${base}/console/excel-templates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) { toast(data.error || '削除に失敗しました', 'error'); return; }
      toast('テンプレートを削除しました');
      refetch();
    } catch { toast('削除に失敗しました', 'error'); }
  }, [refetch]);

  /* ── Update field helper ── */

  const updateField = (idx: number, patch: Partial<ExcelField>) => {
    setEditFields(prev => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));
  };

  /* ── Update mapping helper ── */

  const setMapping = (fieldId: string, table: string, column: string) => {
    setEditMappings(prev => {
      const exists = prev.find(m => m.field_id === fieldId);
      if (!table) return prev.filter(m => m.field_id !== fieldId);
      if (exists) return prev.map(m => m.field_id === fieldId ? { ...m, source_table: table, source_column: column } : m);
      return [...prev, { field_id: fieldId, source_table: table, source_column: column, transform: null }];
    });
  };

  /* ── Render ── */

  return (
    <div className={styles.container}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        {Object.entries(CATEGORIES).map(([key, label]) => (
          <button key={key}
            className={`${styles.filterBtn} ${filter === key ? styles.filterBtnActive : ''}`}
            onClick={() => setFilter(key)}
          >{label}</button>
        ))}
        {canEdit && (
          <button className={styles.addBtn} onClick={() => setShowUpload(true)}>
            Excelアップロード
          </button>
        )}
      </div>

      {/* Template grid */}
      {loading ? <p style={{ color: 'var(--ink2)' }}>読み込み中...</p> : (
        !templates?.length ? (
          <div className={styles.empty}>
            <p>Excel帳票テンプレートがありません</p>
          </div>
        ) : (
          <div className={styles.grid}>
            {templates.map(tpl => (
              <div key={tpl.id} className={styles.card} onClick={() => navigate(`/excel-templates/${tpl.id}/builder`)}>
                <div className={styles.cardHeader}>
                  <span className={styles.cardName}>{tpl.name}</span>
                  <span className={tpl.status === 'active' ? styles.activeBadge : styles.draftBadge}>
                    {tpl.status === 'active' ? '公開中' : '下書き'}
                  </span>
                </div>
                <div className={styles.cardMeta}>
                  <span>{CATEGORIES[tpl.category] || tpl.category}</span>
                  <span>{tpl.file_name}</span>
                  <span>{formatBytes(tpl.file_size)}</span>
                </div>
                <div className={styles.cardFields}>
                  フィールド: {Array.isArray(tpl.fields) ? tpl.fields.length : 0}個 ・ v{tpl.current_version}
                  {tpl.published_at && ` ・ 公開: ${formatDate(tpl.published_at)}`}
                </div>
                <div className={styles.cardActions}>
                  {canEdit && (
                    <button className={styles.deleteBtn} onClick={e => { e.stopPropagation(); handleDelete(tpl.id); }}>削除</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Upload Modal */}
      <Modal open={showUpload} onClose={() => setShowUpload(false)} title="Excelテンプレートアップロード" width={550} footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          <button className={styles.aiBtn} onClick={() => setShowAiCreate(true)}>✨ AI作成</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={styles.actionBtn} onClick={() => setShowUpload(false)}>キャンセル</button>
            <button className={styles.addBtn} onClick={handleUpload} disabled={!uploadFile || uploading}>
              {uploading ? 'アップロード中...' : 'アップロード'}
            </button>
          </div>
        </div>
      }>
        <div className={styles.formGroup}>
          <label>カテゴリ</label>
          <select value={uploadCategory} onChange={e => setUploadCategory(e.target.value)}>
            {Object.entries(CATEGORIES).filter(([k]) => k !== 'all').map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>テンプレート名</label>
          <input value={uploadName} onChange={e => setUploadName(e.target.value)} placeholder="自動: ファイル名から取得" />
        </div>
        <div className={styles.formGroup}>
          <label>説明</label>
          <input value={uploadDescription} onChange={e => setUploadDescription(e.target.value)} placeholder="テンプレートの説明" />
        </div>
        <div className={styles.uploadArea}>
          <input ref={fileInputRef} className={styles.fileInputHidden} type="file" accept=".xlsx,.xls,.docx,.pdf"
            onChange={e => { const f = e.target.files?.[0]; if (f) { setUploadFile(f); if (!uploadName) setUploadName(f.name.replace(/\.\w+$/, '')); fetchSheetNames(f); } }} />
          {!uploadFile ? (
            <>
              <button type="button" className={styles.fileSelectBtn} onClick={() => fileInputRef.current?.click()}>
                📂 Excelファイルを選択
              </button>
              <p className={styles.uploadHint}>対応形式: .xlsx .xls .docx .pdf（最大20MB）</p>
            </>
          ) : (
            <div className={styles.fileInfo}>
              <span className={styles.fileIcon}>📄</span>
              <div className={styles.fileDetails}>
                <div className={styles.fileName}>{uploadFile.name}</div>
                <div className={styles.fileSize}>{formatBytes(uploadFile.size)}</div>
              </div>
              <button type="button" className={styles.fileClear}
                onClick={() => { setUploadFile(null); setSheetList([]); setSelectedSheets({}); if (fileInputRef.current) fileInputRef.current.value = ''; }}>✕</button>
            </div>
          )}
          {sheetList.length > 0 && (
            <div className={styles.sheetSelect}>
              <label className={styles.sheetSelectLabel}>取込シート</label>
              {sheetList.map(name => (
                <label key={name} className={styles.sheetCheckbox}>
                  <input type="checkbox" checked={!!selectedSheets[name]}
                    onChange={e => setSelectedSheets(prev => ({ ...prev, [name]: e.target.checked }))} />
                  {name}
                </label>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Detail / Edit Modal */}
      <Modal open={!!selected} onClose={() => setSelected(null)}
        title={selected ? selected.name : ''} width={900} footer={
        selected ? (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className={styles.actionBtn} onClick={() => setSelected(null)}>閉じる</button>
            {editTab === 'fields' && <button className={styles.addBtn} onClick={saveFields} disabled={saving}>フィールド保存</button>}
            {editTab === 'mappings' && <button className={styles.addBtn} onClick={saveMappings} disabled={saving}>マッピング保存</button>}
            <button className={styles.publishBtn} onClick={() => setShowPublish(true)}>公開</button>
          </div>
        ) : (
          <button className={styles.actionBtn} onClick={() => setSelected(null)}>閉じる</button>
        )
      }>
        {selected && (
          <>
            <div className={styles.cardMeta} style={{ marginBottom: 16 }}>
              <span>{CATEGORIES[selected.category] || selected.category}</span>
              <span>{selected.sheet_name}</span>
              <span>{selected.row_count}行 × {selected.col_count}列</span>
              <span>セル結合: {Array.isArray(selected.merge_cells) ? selected.merge_cells.length : 0}</span>
            </div>

            {/* Tabs */}
            <div className={styles.toolbar} style={{ marginBottom: 12 }}>
              {(['fields', 'mappings', 'preview'] as const).map(tab => (
                <button key={tab}
                  className={`${styles.filterBtn} ${editTab === tab ? styles.filterBtnActive : ''}`}
                  onClick={() => { setEditTab(tab); if (tab === 'preview' && selected) loadPreview(selected.id); }}
                >{tab === 'fields' ? 'フィールド定義' : tab === 'mappings' ? 'マッピング' : 'プレビュー'}</button>
              ))}
            </div>

            {/* Fields tab */}
            {editTab === 'fields' && (
              <div className={styles.fieldList}>
                {editFields.length === 0 ? (
                  <p className={styles.empty}>検出されたフィールドがありません</p>
                ) : editFields.map((field, idx) => (
                  <div key={field.id} className={styles.fieldItem}>
                    <span className={`${styles.fieldCell} ${styles.clickable}`} onClick={() => scrollToField(field.id)} title="プレビューで表示">{field.merge_range || field.cell_ref}</span>
                    {field.sub_label && <span className={styles.subLabel}>{field.sub_label}</span>}
                    <input className={styles.fieldLabel} value={field.label}
                      onChange={e => updateField(idx, { label: e.target.value })}
                      placeholder="ラベル" />
                    <select value={field.field_type}
                      onChange={e => updateField(idx, { field_type: e.target.value as ExcelField['field_type'] })}
                     >
                      {Object.entries(FIELD_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <label className={styles.fieldRequired}>
                      <input type="checkbox" checked={field.required}
                        onChange={e => updateField(idx, { required: e.target.checked })}
                        />
                      必須
                    </label>
                  </div>
                ))}
              </div>
            )}

            {/* Mappings tab */}
            {editTab === 'mappings' && (
              <div className={styles.fieldList}>
                {editFields.map(field => {
                  const mapping = editMappings.find(m => m.field_id === field.id);
                  return (
                    <div key={field.id} className={styles.mappingRow}>
                      <div className={styles.mappingField}>
                        <span className={`${styles.fieldCell} ${styles.clickable}`} onClick={() => scrollToField(field.id)} title="プレビューで表示">{field.cell_ref}</span>
                        <span>{field.label || '(ラベルなし)'}{field.sub_label && <span className={styles.subLabel}> / {field.sub_label}</span>}</span>
                      </div>
                      <span className={styles.mappingArrow}>→</span>
                      <select value={mapping ? `${mapping.source_table}.${mapping.source_column}` : ''}
                        onChange={e => {
                          const [table, col] = e.target.value.split('.');
                          setMapping(field.id, table || '', col || '');
                        }}
                       >
                        <option value="">手動入力</option>
                        {mappableColumns && Object.entries(mappableColumns).map(([table, colMap]) =>
                          Object.entries(colMap).map(([col, label]) => <option key={`${table}.${col}`} value={`${table}.${col}`}>{MAPPABLE_TABLES[table] || table}{label}</option>)
                        )}
                      </select>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Preview tab */}
            {editTab === 'preview' && (
              previewHtml ? (
                <iframe
                  ref={previewRef}
                  className={styles.previewFrame}
                  srcDoc={previewHtml}
                  title="Excel Preview"
                  sandbox="allow-same-origin allow-scripts"
                />
              ) : (
                <div className={styles.empty}>プレビューを読み込み中...</div>
              )
            )}
          </>
        )}
      </Modal>

      {/* Publish Confirm */}
      <Modal open={showPublish} onClose={() => setShowPublish(false)} title="テンプレートを公開" width={450} footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.actionBtn} onClick={() => setShowPublish(false)}>キャンセル</button>
          <button className={styles.publishBtn} onClick={handlePublish}>公開する</button>
        </div>
      }>
        <p style={{ color: 'var(--ink1)', lineHeight: 1.8 }}>
          テンプレートを公開すると、テナント側で利用可能になります。<br />
          公開後もフィールド定義・マッピングの編集は可能です。<br />
          よろしいですか？
        </p>
      </Modal>

      {/* AI作成: 解析モードの選択のみ。解析処理は未実装 */}
      <Modal open={showAiCreate} onClose={() => setShowAiCreate(false)} title="✨ AI作成" width={560} footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.actionBtn} onClick={() => setShowAiCreate(false)}>キャンセル</button>
          <button className={styles.addBtn} onClick={() => toast('解析機能は未実装です', 'info')}>▶ 解析を実行</button>
        </div>
      }>
        <div className={styles.aiTarget}>
          <div className={styles.aiTargetRow}>
            <span className={styles.aiTargetLabel}>対象</span>
            <span className={styles.aiTargetValue}>{uploadFile ? uploadFile.name : 'ファイルが選択されていません'}</span>
          </div>
          {uploadFile && (
            <div className={styles.aiTargetRow}>
              <span className={styles.aiTargetLabel}>サイズ</span>
              <span className={styles.aiTargetValue}>{formatBytes(uploadFile.size)}</span>
            </div>
          )}
        </div>

        <div className={styles.aiModeTitle}>解析モード</div>
        <div className={styles.aiModeList}>
          {AI_MODES.map(m => (
            <label key={m.key} className={`${styles.aiModeItem} ${aiMode === m.key ? styles.aiModeItemActive : ''}`}>
              <input type="radio" name="aiMode" value={m.key}
                checked={aiMode === m.key} onChange={e => setAiMode(e.target.value)} />
              <div className={styles.aiModeBody}>
                <div className={styles.aiModeHead}>
                  <span className={styles.aiModeLabel}>{m.icon} {m.label}</span>
                  <span className={m.key === 'local' ? styles.aiModePriceFree : styles.aiModePrice}>
                    {m.key === 'local' ? m.price : `${m.price} / 1M`}
                  </span>
                </div>
                <div className={styles.aiModeDesc}>{m.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </Modal>
    </div>
  );
}
